import { useState, useEffect } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { logout } from '../../../redux/slices/authSlice';
import alertify from 'alertifyjs';
import UserAvatarMenu from '../../common/UserAvatarMenu';

/**
 * Navbar - Public layout sticky navigation bar
 * 
 * CRITICAL: Uses React Router 5 <Link> component
 * 
 * Features:
 * - Sticky positioning (stays at top on scroll)
 * - Transparent background, becomes opaque on scroll
 * - 6 navigation links (larger font sizes)
 * - 2 auth buttons (Login, Register)
 * - Mobile hamburger menu
 * - Responsive design
 * - Larger logo size
 * 
 * @returns {JSX.Element} Navbar component
 */
const Navbar = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { isAuthenticated, user } = useSelector((state) => state.auth);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  // Mobile logout handler
  const handleMobileLogout = () => {
    closeMobileMenu();
    dispatch(logout());
    alertify.success('Çıkış yapıldı');
    history.push('/');
  };

  // Scroll detection for transparency effect
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { label: 'Ana Sayfa', href: '/' },
    { label: 'Şablonlar', href: '/publictemplates' },
    { label: 'Fiyatlandırma', href: '/pricing' },
    { label: 'SSS', href: '/faq' },
    { label: 'Hakkımızda', href: '/about' },
    { label: 'İletişim', href: '/contact' },
  ];

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <nav 
      className={`sticky top-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-white/50 backdrop-blur-sm shadow-md' 
          : 'bg-transparent'
      }`}
    >
      <div className="container mx-auto px-4 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo - Left (sadece logo, text yok - BÜYÜK) */}
          <div className="flex-shrink-0">
            <Link to="/" className="flex items-center" onClick={closeMobileMenu}>
              <img
                src="/contractcode-logo.png"
                alt="ContractCode"
                className="h-20 w-auto"
              />
            </Link>
          </div>

          {/* Desktop Navigation - Center (BÜYÜK font) */}
          <div className="hidden md:flex items-center space-x-8 lg:space-x-10">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className="text-gray-900 hover:text-primary-600 px-3 py-2 text-lg font-medium transition-colors duration-200"
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Auth Buttons - Right (Desktop) */}
          <div className="hidden md:flex items-center space-x-4">
            {!isAuthenticated ? (
              <>
                <Link
                  to="/login"
                  className="text-gray-900 hover:text-primary-600 px-5 py-2 text-lg font-medium transition-colors duration-200"
                >
                  Giriş Yap
                </Link>
                <Link
                  to="/register"
                  className="bg-primary-500 text-white hover:bg-primary-600 px-6 py-3 rounded-lg text-lg font-semibold transition-all duration-200 shadow-lg hover:shadow-xl"
                >
                  Ücretsiz Başla
                </Link>
              </>
            ) : (
              // ✅ Reusable User Avatar Menu (MUI)
              <UserAvatarMenu showGreeting variant="navbar" />
            )}
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden">
            <button
              onClick={toggleMobileMenu}
              className="text-gray-700 hover:text-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500 rounded-lg p-2"
              aria-label="Toggle mobile menu"
              aria-expanded={isMobileMenuOpen}
            >
              {isMobileMenuOpen ? (
                // Close Icon (X)
                <svg
                  className="h-6 w-6"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                // Hamburger Icon
                <svg
                  className="h-6 w-6"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu - Slide down (BÜYÜK font) */}
        {isMobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-gray-200 bg-white/50 backdrop-blur-sm">
            {/* Mobile Navigation Links */}
            <div className="flex flex-col space-y-2">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={closeMobileMenu}
                  className="text-gray-900 hover:text-primary-600 hover:bg-gray-50 px-4 py-3 text-lg font-medium rounded-lg transition-colors duration-200"
                >
                  {link.label}
                </Link>
              ))}
            </div>

            {/* Mobile Auth Buttons */}
            <div className="flex flex-col space-y-3 mt-4 pt-4 border-t border-gray-200">
              {!isAuthenticated ? (
                <>
                  <Link
                    to="/login"
                    onClick={closeMobileMenu}
                    className="text-gray-900 hover:text-primary-600 hover:bg-gray-50 px-4 py-3 text-lg font-medium rounded-lg transition-colors duration-200 text-center"
                  >
                    Giriş Yap
                  </Link>
                  <Link
                    to="/register"
                    onClick={closeMobileMenu}
                    className="bg-primary-500 text-white hover:bg-primary-600 px-4 py-3 rounded-lg text-lg font-semibold transition-all duration-200 shadow-lg text-center"
                  >
                    Ücretsiz Başla
                  </Link>
                </>
              ) : (
                <>
                  <div className="text-gray-900 px-4 py-2 text-lg font-medium text-center">
                    Merhaba, {user?.firstName}
                  </div>
                  <Link
                    to="/dashboard"
                    onClick={closeMobileMenu}
                    className="text-gray-900 hover:text-primary-600 hover:bg-gray-50 px-4 py-3 text-lg font-medium rounded-lg transition-colors duration-200 text-center"
                  >
                    Dashboard
                  </Link>
                  <Link
                    to="/profile"
                    onClick={closeMobileMenu}
                    className="text-gray-900 hover:text-primary-600 hover:bg-gray-50 px-4 py-3 text-lg font-medium rounded-lg transition-colors duration-200 text-center"
                  >
                    Profil Ayarları
                  </Link>
                  <button
                    onClick={handleMobileLogout}
                    className="bg-red-500 text-white hover:bg-red-600 px-4 py-3 rounded-lg text-lg font-semibold transition-all duration-200 shadow-lg text-center"
                  >
                    Çıkış Yap
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;

